import { sortBy } from 'lodash';
import { Instance, applySnapshot, flow, types } from 'mobx-state-tree';

import {
	ChunksSort,
	deleteChunks,
	getChunks,
	getIndivStudentChunks,
	getStudentChunks,
	postChunks,
	updateChunks,
	updateChunksSort,
} from '@common/api/chunks';
import { ChunkTypesId } from '@common/constants/ChunkTypes';

import TableData from '../TableData';
import Chunk, { IChunkModel, IChunkSnapshotIn } from './Chunk';

interface ITopic {
	topic: IChunkModel;
	chunks: IChunkModel[];
}

const Chunks = types
	.compose(
		TableData,
		types.model({
			data: types.optional(types.array(Chunk), []),
			lesson_work_id: types.maybe(types.number),
			schedule_lesson_id: types.maybe(types.number),
			student_id: types.maybe(types.number),
			isIndiv: types.maybe(types.boolean),
			limit: 100,
		}),
	)
	.named('Chunks')
	.views((self) => ({
		get mapByTopic() {
			const result: Record<number, ITopic> = {};
			self.data.forEach((chunk) => {
				if (result[chunk.topic_num]) {
					result[chunk.topic_num].chunks.push(chunk);
				} else {
					result[chunk.topic_num] = {
						topic: chunk,
						chunks: [],
					};
				}
			});
			return result;
		},
		get fileChunks() {
			return self.data.filter((chunk: IChunkModel) => chunk.chunk_type.id === ChunkTypesId.file);
		},
	}))
	.actions((self) => ({
		remove: flow(function* (ids: number[]) {
			self.loading = true;
			try {
				yield deleteChunks(
					ids.map((id) => ({
						id,
					})),
				);
				applySnapshot(self.data, [
					...self.data.filter((chunk) => !ids.find((id) => chunk.id === id)),
				]);
			} finally {
				self.loading = false;
			}
		}),
		changeSort: flow(function* (reorderedids: ChunksSort[]) {
			self.loading = true;
			try {
				yield updateChunksSort(reorderedids);
				/* applySnapshot(
					self.data,
					[...self.data.filter((chunk) => !ids.find((id) => chunk.id === id))]
				); */
			} finally {
				self.loading = false;
			}
		}),
	}))
	.actions((self) => ({
		_fetch: (query: any) => {
			if (self.student_id) {
				return self.isIndiv ? getIndivStudentChunks({
					lesson_work_id: self.lesson_work_id,
					student_id: self.student_id,
					indiv_schedule_lesson_id: self.schedule_lesson_id,
					...query,
				}) : getStudentChunks({
					lesson_work_id: self.lesson_work_id,
					student_id: self.student_id,
					schedule_lesson_id: self.schedule_lesson_id,
					...query,
				});
			}

			return getChunks({ lesson_work_id: self.lesson_work_id, ...query });
		},
		save: flow(function* (chunks: IChunkSnapshotIn[], isSilent: boolean = false) {
			chunks.forEach((chunk, key) => {
				chunk.sort_id = key + 1;
			});

			const deletedChunks: IChunkModel[] = [];
			const updatedChunks: IChunkSnapshotIn[] = [];
			const newChunks = chunks.filter((chunk) => !chunk.id);
			self.data.forEach((chunkModel) => {
				const updatedChunk = chunks.find((chunk) => chunk.id === chunkModel.id);
				if (updatedChunk) {
					updatedChunks.push(updatedChunk);
				} else {
					deletedChunks.push(chunkModel);
				}
			});

			try {
				if (!isSilent) {
					self.loading = true;
				}
				let newChunksResult = [];
				let updatedChunksResult = [];
				if (deletedChunks.length) {
					yield deleteChunks(
						deletedChunks.map((chunkModel) => ({
							id: chunkModel.id,
						})),
					);
					applySnapshot(self.data, updatedChunks);
				}
				if (newChunks.length) {
					// @TODO: Defaults from add chunk
					newChunksResult = yield postChunks(
						newChunks.map((newChunk) => ({
							lesson_work_id: self.lesson_work_id,
							chunk_type_id: newChunk.chunk_type.id,
							sort_id: newChunk.sort_id,
							name: newChunk.name,
							title: newChunk.title || undefined,
							description: newChunk.description || undefined,
							file_url: newChunk.file_url || undefined,
							image_url: newChunk.image_url || undefined,
							scorm_url: newChunk.scorm_url || undefined,
							video_url: newChunk.video_url || undefined,
							scorms: newChunk.scorms || undefined,
							topic_num: newChunk.topic_num || 1,
							information_scorm: newChunk.information_scorm || false,
							slide_for_teacher: newChunk.slide_for_teacher || '',
							media_url: newChunk.media_url || undefined,
							scorm_vendor: newChunk.scorm_vendor || undefined,
						})),
					);
				}
				if (updatedChunks.length) {
					updatedChunksResult = yield updateChunks(
						updatedChunks.map((updatedChunk) => {
							const oldChunk = self.data.find((chunkModel) => chunkModel.id === updatedChunk.id);
							return {
								id: updatedChunk.id,
								sort_id: updatedChunk.sort_id,
								name: updatedChunk.name,
								title: updatedChunk.title || undefined,
								description: updatedChunk.description || undefined,
								file_url:
									oldChunk.file_url === updatedChunk.file_url
										? undefined
										: updatedChunk.file_url || undefined,
								image_url:
									oldChunk.image_url === updatedChunk.image_url
										? undefined
										: updatedChunk.image_url || undefined,
								scorm_url:
									oldChunk.scorm_url === updatedChunk.scorm_url
										? undefined
										: updatedChunk.scorm_url || undefined,
								video_url:
									oldChunk.video_url === updatedChunk.video_url
										? undefined
										: updatedChunk.video_url || undefined,
								media_url:
									oldChunk.media_url === updatedChunk.media_url
										? undefined
										: updatedChunk.media_url || undefined,
								scorms: updatedChunk.scorms || undefined,
								topic_num: updatedChunk.topic_num || 1,
								information_scorm: updatedChunk.information_scorm || false,
								slide_for_teacher: updatedChunk.slide_for_teacher || '',
								scorm_vendor: updatedChunk.scorm_vendor || undefined,
							};
						}),
					);
				}
				applySnapshot(self.data, sortBy([...newChunksResult, ...updatedChunksResult], 'sort_id'));
			} finally {
				self.loading = false;
			}
		}),
	}));

export default Chunks;

export interface IChunksModel extends Instance<typeof Chunks> {}
